"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  type TooltipContentProps,
  Line,
  ComposedChart,
} from "recharts";
import { BillingService } from "@/services/BillingService";
import CustomCombobox from "@/components/CustomCombobox";
import { BarChart2 } from "lucide-react";
import { numberToMoney } from "@/util/moneyConversion";
import type { BillingsMonthlyOutput } from "@/interfaces/billing";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BaseComponentProps } from "@/interfaces/dashboard";
import { FinancialColors } from "@/util/chartColors";

type TooltipPayloadEntry = {
  dataKey: string;
  name: string;
  value: number;
  color: string;
};

const monthsOptions = [
  { label: "Último mês", value: "1" },
  { label: "Últimos 3 meses", value: "3" },
  { label: "Últimos 6 meses", value: "6" },
  { label: "Últimos 12 meses", value: "12" },
];

export const FinancialOverviewChart: React.FC<BaseComponentProps> = ({
  isLoading = false,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>("3");
  const [chartData, setChartData] = useState<BillingsMonthlyOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<"stacked" | "line">("stacked");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new BillingService();
        const response = await service.getMonthly({
          lastMonths: selectedPeriod,
        });

        setChartData(response);
      } catch {
        setError("Falha ao carregar dados financeiros");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod]);

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipContentProps<number, string>) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="bg-white p-4 border border-gray-200 rounded-md shadow-sm">
        <p className="font-bold text-gray-900 mb-2">{label}</p>
        {(payload as TooltipPayloadEntry[]).map((entry) => (
          <p
            key={entry.dataKey}
            className="text-gray-700 flex items-center"
            style={{ color: entry.color }}
          >
            <span
              className="inline-block w-3 h-3 mr-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            {entry.name}: {numberToMoney(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  // Processa os dados para o gráfico
  const processedData = chartData
    .sort(
      (a, b) =>
        new Date(a.year, a.month - 1).getTime() -
        new Date(b.year, b.month - 1).getTime()
    )
    .map((entry) => ({
      name: `${entry.month.toString().padStart(2, "0")}/${entry.year}`,
      Recebido: entry.paymentReceived,
      Despesas: entry.totalExpanses,
      Pendente: entry.pendingPayments,
      Lucro: entry.paymentReceived - entry.totalExpanses,
    }));

  if (isLoading || loading) {
    return (
      <Card>
        <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Visão Geral Financeira</CardTitle>
          <div className="w-[200px]">
            <Skeleton className="h-9 w-full" />
          </div>
        </CardHeader>
        <CardContent className="h-[400px] flex items-center justify-center">
          <Skeleton className="h-[350px] w-full rounded-md" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Visão Geral Financeira</CardTitle>
          <CustomCombobox
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            options={monthsOptions}
            placeholder="Selecione o período"
          />
        </CardHeader>
        <CardContent className="h-[400px] flex items-center justify-center text-red-500">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <CardTitle className="flex items-center">
          <BarChart2 className="h-5 w-5 mr-2 text-orange-500" />
          Visão Geral Financeira
        </CardTitle>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <Tabs
            value={chartType}
            onValueChange={(value) => setChartType(value as "stacked" | "line")}
            className="w-full sm:w-[180px]"
          >
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="stacked">Empilhado</TabsTrigger>
              <TabsTrigger value="line">Linhas</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="w-full sm:w-auto">
            <CustomCombobox
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              options={monthsOptions}
              placeholder="Selecione o período"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <motion.div
          key={chartType}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ResponsiveContainer width="100%" height={400}>
            {chartType === "stacked" ? (
              <BarChart data={processedData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="name"
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(value) => numberToMoney(value)}
                  tickLine={false}
                />
                <Tooltip content={CustomTooltip} />
                <Legend />
                <Bar
                  dataKey="Recebido"
                  fill={FinancialColors.income}
                  name="Pagamentos Recebidos"
                  stackId="a"
                  animationDuration={1500}
                />
                <Bar
                  dataKey="Despesas"
                  fill={FinancialColors.expense}
                  name="Total Despesas"
                  stackId="a"
                  animationDuration={1500}
                  animationBegin={300}
                />
                <Bar
                  dataKey="Pendente"
                  fill={FinancialColors.pending}
                  name="Pagamentos Pendentes"
                  stackId="a"
                  animationDuration={1500}
                  animationBegin={600}
                />
              </BarChart>
            ) : (
              <ComposedChart data={processedData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="name"
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(value) => numberToMoney(value)}
                  tickLine={false}
                />
                <Tooltip content={CustomTooltip} />
                <Legend />
                <Bar
                  dataKey="Recebido"
                  fill={FinancialColors.income}
                  name="Pagamentos Recebidos"
                  animationDuration={1500}
                />
                <Bar
                  dataKey="Despesas"
                  fill={FinancialColors.expense}
                  name="Total Despesas"
                  animationDuration={1500}
                  animationBegin={300}
                />
                <Line
                  type="monotone"
                  dataKey="Lucro"
                  stroke={FinancialColors.profit}
                  name="Lucro"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  animationDuration={2000}
                  animationBegin={600}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </motion.div>
      </CardContent>
    </Card>
  );
};
